import { getApiBaseUrl } from './api';

export const authService = {
  // Login user
  async login(username, password) {
    const response = await fetch(`${getApiBaseUrl()}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ username, password })
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }
    
    const data = await response.json();
    
    // Store token and user info
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify({
      username: data.username,
      role: data.role,
      enterpriseIdFk: data.enterpriseIdFk ?? null
    }));
    
    return data;
  },

  // Logout user
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Get current user
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },

  async refreshCurrentUser() {
    const response = await this.apiCall('/auth/me');
    if (!response.ok) return null;
    const data = await response.json();
    const user = {
      username: data.username,
      role: data.role,
      enterpriseIdFk: data.enterpriseIdFk ?? null
    };
    localStorage.setItem('user', JSON.stringify(user));
    return user;
  },

  // Get auth token
  getToken() {
    return localStorage.getItem('token');
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  // Check if user has admin role (SUPER_ADMIN is a superset of ADMIN, so it counts too)
  isAdmin() {
    const user = this.getCurrentUser();
    return user && (
      user.role === 'ADMIN' || user.role === 'ROLE_ADMIN' ||
      user.role === 'SUPER_ADMIN' || user.role === 'ROLE_SUPER_ADMIN'
    );
  },

  // Check if user has super admin role
  isSuperAdmin() {
    const user = this.getCurrentUser();
    return user && (user.role === 'SUPER_ADMIN' || user.role === 'ROLE_SUPER_ADMIN');
  },

  // USER accounts are scoped to a single enterprise (user.enterpriseIdFk);
  // ADMIN / SUPER_ADMIN see every enterprise.
  isEnterpriseScopedUser(user = this.getCurrentUser()) {
    return !!user && (user.role === 'USER' || user.role === 'ROLE_USER');
  },

  // Get auth headers for API calls
  getAuthHeaders() {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };
  },

  // Make authenticated API call
  async apiCall(url, options = {}) {
    const token = this.getToken();
    
    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers
      },
      ...options
    };

    const response = await fetch(`${getApiBaseUrl()}${url}`, config);
    
    // Handle 401 unauthorized - session expired or invalid token
    if (response.status === 401) {
      console.error('Unauthorized: Session expired or invalid token');
      this.logout();
      window.location.href = '/login';
      throw new Error('Session expired. Please login again.');
    }
    
    return response;
  }
};
